'use client'

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react'
import { fetchAppFreeVhfProgress } from '@/lib/app-data/free-vhf-progress'
import { useAuth } from '@/lib/auth/auth-context'
import {
  FREE_VHF_SCENARIO_IDS_CLIENT,
  FREE_VHF_TOTAL_LESSONS_CLIENT,
} from '@/lib/free-vhf-scenario-ids'
import type { FreeVhfProgressState } from '@/hooks/useFreeVhfProgress'

const FreeVhfProgressContext = createContext<FreeVhfProgressState | null>(null)

export function FreeVhfProgressProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth()
  const [completedScenarioIds, setCompletedScenarioIds] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProgress = useCallback(() => {
    if (!user) {
      setCompletedScenarioIds([])
      setLoading(false)
      setError(null)
      return
    }
    setLoading(true)
    setError(null)

    fetchAppFreeVhfProgress()
      .then((rows) => {
        setCompletedScenarioIds(rows.map((r) => r.scenario_id))
        setLoading(false)
      })
      .catch(() => {
        setCompletedScenarioIds([])
        setLoading(false)
        setError('Failed to load progress')
      })
  }, [user])

  useEffect(() => {
    queueMicrotask(() => fetchProgress())
  }, [fetchProgress])

  const completedSet = new Set(completedScenarioIds)
  const completedCount = completedScenarioIds.length
  const totalLessons = FREE_VHF_TOTAL_LESSONS_CLIENT
  const percent = totalLessons > 0 ? Math.round((completedCount / totalLessons) * 100) : 0
  const nextScenarioId =
    FREE_VHF_SCENARIO_IDS_CLIENT.find((id) => !completedSet.has(id)) ?? null

  const value: FreeVhfProgressState = {
    completedScenarioIds,
    completedCount,
    totalLessons,
    percent,
    nextScenarioId,
    loading,
    error,
    mutate: fetchProgress,
  }

  return (
    <FreeVhfProgressContext.Provider value={value}>
      {children}
    </FreeVhfProgressContext.Provider>
  )
}

export function useFreeVhfProgressContext(): FreeVhfProgressState | null {
  return useContext(FreeVhfProgressContext)
}
